import {
  Controller,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues
} from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import { FormFieldUserSelect, type FormFieldUserSelectProps } from '../FormFieldUserSelect';

export interface FormUserSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<FormFieldUserSelectProps, 'name' | 'value' | 'onChange' | 'error'> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: string;
}

/**
 * React Hook Form adapter for UserSelect with Field integration
 * Automatically fetches users and handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormUserSelect
 *   control={control}
 *   name="manager"
 *   label="Manager"
 *   required
 * />
 * ```
 */
export function FormUserSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  defaultValue,
  // Defaults to true — RHF selects live in edit forms, where an existing
  // assignment must never render as a blank field.
  showUnselectableValue = true,
  ...fieldProps
}: FormUserSelectProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  // Display from the raw watched value, not Controller's field.value: the
  // latter falls back to the field's original value whenever the live value is
  // undefined (RHF useWatch defaultValue masking), so clearing an optional
  // select would keep showing the old value even though the form state is
  // correctly cleared.
  const value = useWatch({ control, name });

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => {
        const errorMessage = fieldErrorMessage(fieldState.error);

        return (
          <FormFieldUserSelect
            {...fieldProps}
            id={String(name)}
            name={String(name)}
            showUnselectableValue={showUnselectableValue}
            value={value as string | string[] | undefined}
            onChange={field.onChange}
            error={errorMessage}
            ref={field.ref}
          />
        );
      }}
    />
  );
}

FormUserSelect.displayName = 'FormUserSelect';
